import {Injectable, NgZone} from '@angular/core';
import {App} from '@capacitor/app';
import type {PluginListenerHandle} from '@capacitor/core';
import {isNativeCapacitorPlatform} from '../shared/platform';
import {AuthSessionInvalidationService} from './auth-session-invalidation.service';
import {AuthError, AuthService} from './auth.service';

export interface AuthAppStatePort {
  addListener(
    eventName: 'appStateChange',
    listener: (state: {isActive: boolean}) => void,
  ): Promise<PluginListenerHandle>;
}

@Injectable({providedIn: 'root'})
export class AuthSessionResumeService {
  private appStateListener?: PluginListenerHandle;
  private initialized = false;

  constructor(
    private readonly authService: AuthService,
    private readonly ngZone: NgZone,
    private readonly sessionInvalidationService: AuthSessionInvalidationService,
  ) {}

  async initialize(app: AuthAppStatePort = App): Promise<void> {
    if (this.initialized || !isNativeCapacitorPlatform()) {
      return;
    }

    this.initialized = true;
    try {
      const listener = await app.addListener('appStateChange', ({isActive}) => {
        if (isActive) {
          void this.ngZone.run(() => this.renewSessionIfNeeded()).catch(() => undefined);
        }
      });

      if (!this.initialized) {
        await listener.remove();
        return;
      }

      this.appStateListener = listener;
    } catch {
      this.initialized = false;
    }
  }

  async destroy(): Promise<void> {
    this.initialized = false;
    const listener = this.appStateListener;
    this.appStateListener = undefined;
    await listener?.remove();
  }

  private async renewSessionIfNeeded(): Promise<void> {
    if (!this.authService.session || !this.authService.shouldRefreshAccessToken()) {
      return;
    }

    try {
      await this.authService.refreshSession();
    } catch (error) {
      if (error instanceof AuthError && error.status === 401) {
        await this.sessionInvalidationService.handleInvalidSession();
      }
    }
  }
}
